import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Pricing.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Eén lijn-icoon per stap in pricing.setupSteps (nl/en.json), in dezelfde
// volgorde en stijl als de featurelijst op de homepage ("features"-sectie
// met camera/kalender/... icoontjes) - hier in het donkergroene
// Setup-thema (#1b5e20) i.p.v. het gewone blauw.
const SETUP_STEP_ICON_COLOR = '#1e88e5';
const SETUP_STEP_ICONS = [
  // Camera - professionele fotografie
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 8H7L8.5 5.5H15.5L17 8H20C20.55 8 21 8.45 21 9V18C21 18.55 20.55 19 20 19H4C3.45 19 3 18.55 3 18V9C3 8.45 3.45 8 4 8Z" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="12" cy="13.5" r="3.5" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
  </svg>,
  // Document - advertenties die opvallen
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="3" width="14" height="18" rx="2" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8.5 8H15.5M8.5 12H15.5M8.5 16H12.5" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Netwerk - één centraal systeem
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="12" r="3" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <circle cx="18" cy="6" r="3" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <circle cx="18" cy="18" r="3" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8.7 10.8L15.3 7.2M8.7 13.2L15.3 16.8" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
  </svg>,
  // Kalender - kalender en prijsstrategie
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M3 9H21" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8 2V6M16 2V6" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M7 13H9M11 13H13M15 13H17M7 17H9M11 17H13" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Chatbubbel - automatische gastcommunicatie
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 5H20V16H9L5 19.5V16H4V5Z" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 9.5H16M8 12.5H13" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Vinkje - testen en opleveren
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8 12.3L10.8 15L16 9.5" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Wereldbol - eigen boekingswebsite
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
    <path d="M3 12H21M12 3C14.5 5.7 15.8 8.8 15.8 12C15.8 15.2 14.5 18.3 12 21C9.5 18.3 8.2 15.2 8.2 12C8.2 8.8 9.5 5.7 12 3Z" stroke={SETUP_STEP_ICON_COLOR} strokeWidth="1.5" />
  </svg>,
];

// Eén lijn-icoon per item in pricing.automationFeatures (nl/en.json), zelfde
// stijl als SETUP_STEP_ICONS hierboven maar in het donkerblauwe
// Automation-thema (#0d47a1) i.p.v. donkergroen.
const AUTOMATION_FEATURE_ICON_COLOR = '#0d47a1';
const AUTOMATION_FEATURE_ICONS = [
  // Kalender - kalender en channel manager
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M3 9H21" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8 2V6M16 2V6" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M7 13H9M11 13H13M15 13H17M7 17H9M11 17H13" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Trendpijl - dynamische prijszetting
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 17L10 11L14 15L20 8" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 8H20V13" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Chatbubbel - gastcommunicatie
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 5H20V16H9L5 19.5V16H4V5Z" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 9.5H16M8 12.5H13" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Hangslot - toegangsbeheer
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8 11V7.5C8 5.01 10.01 3 12.5 3C14.99 3 17 5.01 17 7.5V11" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1.6" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M12 17.1V18.8" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Sparkle - schoonmaak
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.1" strokeLinejoin="round" />
  </svg>,
  // Staafdiagram - prestatie-opvolging
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 20V10M10 20V4M16 20V13M21 20H3" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Megafoon - advertentie-optimalisatie
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 10V14H6L14 18V6L6 10H3Z" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M17 9.5C18 10.5 18 13.5 17 14.5" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M9 18.5L10 21H8L7 18.5" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Headset - doorlopende ondersteuning
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 13V12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12V13" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
    <rect x="2.5" y="13" width="4" height="6" rx="1.5" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <rect x="17.5" y="13" width="4" height="6" rx="1.5" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M20 19V20C20 21.1 19.1 22 18 22H14" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  // Rapport met mini-grafiek - maandelijkse rapportering
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" />
    <path d="M8 15V11M12 15V8M16 15V13" stroke={AUTOMATION_FEATURE_ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
];

// Eén eenvoudige, telefoon-vrije visual per stap in pricing.accessSteps
// (nl/en/fr.json) - na twee eerdere afgekeurde stijlen (abstracte
// lijn-icoontjes, daarna telefoonmockup-"screenshots") koos de klant expliciet
// voor géén telefoonmockups meer, met per stap een heel specifieke, simpele
// weergave: kalender met blauwe boekingsbalkjes, kale gastcode-kaartjes, een
// simpele e-mail-layout, en (voor stap 4/6) een meldingskaartje zonder
// belicoontje naast een echte, gratis hotlinked stockfoto (Unsplash). De
// demo-tekst hierin (namen/codes) is bewust hardcoded en taalonafhankelijk,
// net als voorheen bij de telefoonmockups.
// Was voorheen een vaste array (module-scope, dus zonder toegang tot t()) met
// bewust hardcoded, taalonafhankelijke demo-tekst. Op vraag van de klant is
// dat toch niet correct: enkel de demo-NAMEN/code/adres mogen taalonafhankelijk
// blijven (Emma, 482917, Kerkstraat 12, emma@gast.com - net als bij de
// telefoonmockups voorheen), maar de omringende UI-tekst (labels, zinnen,
// badges) moet wél meevertalen - vandaar nu een functie die t() meekrijgt,
// zie pricing.accessVisual (nl/en/fr.json).
const getAccessStepVisuals = (t) => {
  const guestName = 'Emma';
  const guestEmail = 'emma@gast.com';
  const propertyName = 'Kerkstraat 12';
  const code = '482917';
  return [
  // 1. Boeking komt binnen - kalender met de boeking als blauwe balkjes
  <div className="access-visual access-visual-calendar">
    <div className="access-cal-month">{t('pricing.accessVisual.calendarMonth')}</div>
    <div className="access-cal-weekdays">
      {t('pricing.accessVisual.weekdays', { returnObjects: true }).map((d, i) => <span key={i}>{d}</span>)}
    </div>
    <div className="access-cal-grid">
      {Array.from({ length: 35 }).map((_, i) => {
        const day = i + 1;
        const valid = day <= 31;
        const booked = day >= 9 && day <= 12;
        return (
          <div key={i} className={`access-cal-cell ${!valid ? 'empty' : ''} ${booked ? 'booked' : ''}`}>
            {valid && <span className="access-cal-daynum">{day}</span>}
          </div>
        );
      })}
    </div>
  </div>,
  // 2. Unieke code aangemaakt - kaal gastcode-kaartje
  <div className="access-visual access-visual-code-card">
    <div className="access-code-card-label">{t('pricing.accessVisual.codeCardLabel')}</div>
    <div className="access-code-card-digits">{code}</div>
    <div className="access-code-card-meta">{t('pricing.accessVisual.codeCardMeta', { name: guestName })}</div>
  </div>,
  // 3. Gast checkt zelf in - simpele layout van de bevestigingsmail
  <div className="access-visual access-visual-email">
    <div className="access-email-header">
      <div className="access-email-row"><span className="access-email-label">{t('pricing.accessVisual.emailFrom')}</span><span className="access-email-value">SmartStays</span></div>
      <div className="access-email-row"><span className="access-email-label">{t('pricing.accessVisual.emailTo')}</span><span className="access-email-value">{guestEmail}</span></div>
      <div className="access-email-subject">{t('pricing.accessVisual.emailSubject', { property: propertyName })}</div>
    </div>
    <div className="access-email-body">
      <p className="access-email-greeting">{t('pricing.accessVisual.emailGreeting', { name: guestName })}</p>
      <p className="access-email-text">{t('pricing.accessVisual.emailIntro')}</p>
      <div className="access-email-code">{code}</div>
      <p className="access-email-text">{t('pricing.accessVisual.emailValidity')}</p>
    </div>
  </div>,
  // 4. Melding bij check-in - de volledige incheck-foto, met daarover een
  //    subtiele, doorschijnende meldingsbanner (SmartStays-logo, geen
  //    belicoontje) zoals je die op een iPhone-lockscreen zou krijgen.
  <div className="access-visual access-visual-photo-full">
    <img
      src="/incheck.png"
      alt={t('pricing.accessVisual.checkinPhotoAlt')}
      loading="lazy"
    />
    <div className="access-notif-overlay">
      <img src="/app-icon-rounded.png" alt="" className="access-notif-overlay-icon" />
      <div className="access-notif-overlay-body">
        <div className="access-notif-overlay-top-row">
          <span className="access-notif-overlay-app">SmartStays</span>
          <span className="access-notif-overlay-time">{t('pricing.accessVisual.notifNow')}</span>
        </div>
        <div className="access-notif-overlay-text">{t('pricing.accessVisual.notifCheckin', { name: guestName, property: propertyName })}</div>
      </div>
    </div>
  </div>,
  // 5. Code vervalt automatisch - zelfde kaal kaartje, code doorstreept
  <div className="access-visual access-visual-code-card">
    <div className="access-code-card-label">{t('pricing.accessVisual.codeCardLabel')}</div>
    <div className="access-code-card-digits expired">{code}</div>
    <div className="access-code-card-meta">{t('pricing.accessVisual.codeCardMetaExpired', { name: guestName })}</div>
    <span className="access-code-card-expired-badge">{t('pricing.accessVisual.expiredBadge')}</span>
  </div>,
  // 6. Schoonmaak met eigen code - klein opmerkingszinnetje bovenaan + de
  //    volledige (luxueuzere) slaapkamerfoto met dezelfde meldingsbanner-stijl
  //    als stap 4 erover.
  <div className="access-visual access-visual-step6">
    <div className="access-step-cleaning-note">{t('pricing.accessVisual.cleaningNote')}</div>
    <div className="access-visual-photo-full">
      <img
        src="https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?fm=jpg&q=80&w=900&auto=format&fit=crop"
        alt={t('pricing.accessVisual.cleaningPhotoAlt')}
        loading="lazy"
      />
      <div className="access-notif-overlay access-notif-overlay-top-left">
        <img src="/app-icon-rounded.png" alt="" className="access-notif-overlay-icon" />
        <div className="access-notif-overlay-body">
          <div className="access-notif-overlay-top-row">
            <span className="access-notif-overlay-app">SmartStays</span>
            <span className="access-notif-overlay-time">{t('pricing.accessVisual.notifNow')}</span>
          </div>
          <div className="access-notif-overlay-text">{t('pricing.accessVisual.notifCleanerArrived', { property: propertyName })}</div>
        </div>
      </div>
    </div>
  </div>,
  ];
};

// Stappenplan: links de 6 stappen als genummerde lijst (het is echt een
// volgorde), rechts het bijbehorende beeld. De actieve stap klapt open met
// zijn uitleg. Swipen op het beeld (touch) wisselt ook van stap.
function AccessStepsCarousel({ steps, screens }) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const touchStartX = React.useRef(null);
  const touchDeltaX = React.useRef(0);
  const total = steps.length;
  const goTo = (i) => setActiveStep(Math.max(0, Math.min(total - 1, i)));

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchDeltaX.current = 0; };
  const handleTouchMove = (e) => { if (touchStartX.current !== null) touchDeltaX.current = e.touches[0].clientX - touchStartX.current; };
  const handleTouchEnd = () => {
    if (touchDeltaX.current > 40) goTo(activeStep - 1);
    else if (touchDeltaX.current < -40) goTo(activeStep + 1);
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  return (
    <div className="access-flow">
      <div className="access-flow-side">
      <div className="access-flow-nav">
        <button type="button" className="access-flow-arrow" onClick={() => goTo(activeStep - 1)} disabled={activeStep === 0} aria-label={t('pricing.accessStepPrev')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 15L12 8L19 15" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" className="access-flow-arrow" onClick={() => goTo(activeStep + 1)} disabled={activeStep === total - 1} aria-label={t('pricing.accessStepNext')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 9L12 16L19 9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <ol className="access-flow-list">
        {steps.map((step, i) => (
          <li key={i} className={`access-flow-item ${i === activeStep ? 'active' : ''} ${i < activeStep ? 'done' : ''}`}>
            <button
              type="button"
              className="access-flow-btn"
              onClick={() => goTo(i)}
              aria-current={i === activeStep ? 'step' : undefined}
              aria-label={t('pricing.accessStepOf', { current: i + 1, total })}
            >
              <span className="access-flow-num">{i + 1}</span>
              <span className="access-flow-body">
                <span className="access-flow-title">{step.title}</span>
                <span className="access-flow-collapse"><span className="access-flow-text">{step.text}</span></span>
              </span>
            </button>
          </li>
        ))}
      </ol>
      </div>
      <div className="access-flow-stage-wrap">
        <div
          className="access-flow-stage"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {screens.map((screen, i) => (
            <div key={i} className={`access-flow-screen ${i === activeStep ? 'active' : ''}`} aria-hidden={i !== activeStep}>
              {screen}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // De 4 tabbladen, in weergave-volgorde: Setup (uiterst links, donkergroen
  // thema) - Toegangsbeheer - Schoonmaak - Automation (uiterst rechts,
  // donkerblauw thema). "color" hier is de kleur van het schuivende
  // "pilletje" achter de knoppen (zie .pricing-tab-slider hieronder).
  const TABS = [
    { key: 'toegangsbeheer', labelKey: 'pricing.tabs.accessManagement', color: '#12284a' },
    { key: 'schoonmaak', labelKey: 'pricing.tabs.cleaning', color: '#12284a' },
    { key: 'automation', labelKey: 'pricing.tabs.automation', color: '#12284a' },
  ];
  const TAB_KEYS = TABS.map((tb) => tb.key);

  // Welk tabblad actief is wordt bepaald door de URL (?tab=...), zodat de
  // "Aanbod"-dropdown in de navbar rechtstreeks naar het juiste tabblad kan
  // linken. Standaard: toegangsbeheer.
  const params = new URLSearchParams(location.search);
  const tabParam = params.get('tab');
  const [activeTab, setActiveTab] = useState(TAB_KEYS.includes(tabParam) ? tabParam : (tabParam === 'setup' ? 'automation' : 'toegangsbeheer'));

  useEffect(() => {
    document.title = 'SmartStays - Diensten en tarieven';
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search).get('tab');
    if (TAB_KEYS.includes(p)) {
      setActiveTab(p);
    } else if (p === 'setup') {
      // Setup is nu een optie binnen Automation.
      setActiveTab('automation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    navigate(`/selection?tab=${tab}`, { replace: true });
  };

  // De 7 Setup-stappen (nl/en.json): de eerste 6 worden als tegel getoond
  // (3 naast elkaar, 2 rijen), de 7de ("eigen boekingswebsite") apart als
  // klein balkje - zie de Setup-tab hieronder. Eenmalig opgehaald i.p.v.
  // meermaals dezelfde t()-call met returnObjects in de JSX te herhalen.
  const setupSteps = t('pricing.setupSteps', { returnObjects: true });

  // Alle getoonde prijzen zijn excl. btw; hier tonen we er subtiel (lichtgrijs)
  // de incl.-btw-tegenhanger bij (21% btw), i.p.v. enkel een "excl. btw"-label.
  const toInclVat = (price) => (price * 1.21).toFixed(2);

  // Schoonmaakprijzen per type woning, en aanvullende diensten. Geen
  // oppervlaktes meer (op vraag van de klant weggehaald) - enkel het type en
  // de vaste richtprijs (excl. btw), plus een hotelkamer-tarief bovenaan.
  const cleaningRows = [
    { type: 'hotelRoom', price: 45 },
    { type: 'type2', price: 70 },
    { type: 'type3', price: 95 },
    { type: 'type4', price: 120 },
    { type: 'type5', onRequest: true },
  ];

  const additionalServiceRows = [
    { key: 'lastMinute', priceKey: 'onRequest' },
    { key: 'windowWashing', priceKey: 'onRequest' },
  ];

  return (
    <div className={`pricing-container pricing-container-${activeTab}`}>
      <Navbar />

      <div className="pricing-content">
        <h1>{t('pricing.title')}</h1>

        <div className="pricing-tab-buttons">
          <div
            className="pricing-tab-slider"
            style={{
              width: `calc((100% - ${TABS.length * 8}px) / ${TABS.length})`,
              transform: `translateX(calc(${TABS.findIndex((tb) => tb.key === activeTab)} * (100% + 8px)))`,
              background: TABS.find((tb) => tb.key === activeTab)?.color,
            }}
          ></div>
          {TABS.map((tb) => (
            <button
              key={tb.key}
              className={`pricing-tab-btn ${activeTab === tb.key ? 'active' : ''}`}
              onClick={() => selectTab(tb.key)}
            >
              {t(tb.labelKey)}
            </button>
          ))}
        </div>

        {activeTab === 'toegangsbeheer' && (
          <div className="pricing-tab-panel access-theme" key="toegangsbeheer-panel">
          <section className="product-section access-hero-section">
            <h2 className="pricing-hero-title">{t('pricing.tabs.accessManagement')}</h2>
            <p className="pricing-subtitle">
              {t('pricing.subtitlePart1')} <strong>€9.99{t('pricing.perMonth')}</strong> <span className="vat-note-inline">€{toInclVat(9.99)}{t('pricing.perMonth')} {t('pricing.inclVat')}</span> {t('pricing.subtitlePart2')}
            </p>
          </section>
          <div>
            {/* Chronologisch stappenplan i.p.v. de vroegere losse
                "Gebruiksgemak/Strak design/Betrouwbare beveiliging"-tegels,
                zie pricing.accessSteps (nl/en/fr.json). Geen apart titel/
                introzinnetje meer boven de kaart - de kaart spreekt op
                zichzelf. */}
            <section className="product-section access-steps-section">
              <AccessStepsCarousel
                steps={t('pricing.accessSteps', { returnObjects: true })}
                screens={getAccessStepVisuals(t)}
              />
            </section>

            {/* Gratis begeleiding - sinds de verhuurder zelf zijn smart lock
                aanschaft/beheert (i.p.v. dat SmartStays het slot koopt en
                installeert), helpen we gratis bij het kiezen, installeren en
                koppelen ervan in de app. Zelfde stijl als de opstartkost-balk
                bij Setup/Automation (.tier-price-box). */}
            <section className="product-section">
              <div className="tier-price-box">
                <strong>{t('pricing.accessGuidance.title')}</strong>
                <span>{t('pricing.accessGuidance.text')}</span>
              </div>
            </section>

            {/* Wat je te zien krijgt in de app: een korte featurelijst +
                mockup van het "Smart lock instellingen"-scherm (merk +
                smartlock-id + live status), naar hetzelfde patroon als de
                Automation-appshowcase hierboven (.app-mockup). */}
            <section className="product-section">
              <h2>{t('pricing.accessAppTitle')}</h2>
              <p className="section-description">{t('pricing.accessAppIntro')}</p>
              <div className="automation-app-showcase">
                <ul className="tier-feature-list app-feature-list">
                  {t('pricing.accessAppFeatures', { returnObjects: true }).map((feature, i) => (
                    <li key={i}><span className="tier-check">✓</span> {feature}</li>
                  ))}
                </ul>

                <div className="app-mockup access-app-mockup">
                  <div className="app-mockup-topbar">
                    <span className="app-mockup-dot app-mockup-dot-red"></span>
                    <span className="app-mockup-dot app-mockup-dot-yellow"></span>
                    <span className="app-mockup-dot app-mockup-dot-green"></span>
                    <span className="app-mockup-title">{t('pricing.accessMockup.windowTitle')}</span>
                  </div>
                  <div className="app-mockup-body">
                    <div className="access-mockup-row">
                      <span className="access-mockup-label">{t('pricing.accessMockup.brandLabel')}</span>
                      <span className="access-mockup-value access-mockup-brand">
                        <img src="/Nuki_LOGO.jpg" alt="Nuki" className="access-mockup-brand-logo" />
                      </span>
                    </div>
                    <div className="access-mockup-row">
                      <span className="access-mockup-label">{t('pricing.accessMockup.idLabel')}</span>
                      <span className="access-mockup-value">••••42891</span>
                    </div>
                    <div className="access-mockup-row">
                      <span className="access-mockup-label">{t('pricing.accessMockup.statusLabel')}</span>
                      <span className="access-mockup-value access-mockup-status-ok">{t('pricing.accessMockup.statusValue')}</span>
                    </div>
                    <div className="access-mockup-row">
                      <span className="access-mockup-label">{t('pricing.accessMockup.batteryLabel')}</span>
                      <span className="access-mockup-value">92%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Ondersteunde merken - Nuki en Tedee, met ruimte voor
                toekomstige merken. Geen configurator/prijzen meer: de
                verhuurder schaft het slot nu zelf aan. */}
            <section className="product-section">
              <h2>{t('pricing.brandsTitle')}</h2>
              <p className="section-description">{t('pricing.brandsIntro')}</p>
              <div className="brand-grid">
                <div className="brand-card">
                  <img src="/Nuki_LOGO.jpg" alt="Nuki" className="brand-card-logo" />
                </div>
                <div className="brand-card">
                  <img src="/tedee_logo.png" alt="Tedee" className="brand-card-logo" />
                </div>
              </div>
            </section>
          </div>
          </div>
        )}

        {activeTab === 'schoonmaak' && (
          <div className="pricing-tab-panel schoonmaak-theme" key="schoonmaak-panel">
            <section className="product-section schoonmaak-hero-section">
              <h2 className="pricing-hero-title">{t('pricing.cleaningTitle')}</h2>
              <p className="pricing-subtitle">{t('pricing.cleaningText1')} {t('pricing.cleaningText2')}</p>
            </section>

            {/* Schoonmaakprijzen per type woning */}
            <section className="product-section">
              <div className="cleaning-pricing-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('pricing.cleaningTable.headerType')}</th>
                      <th>{t('pricing.cleaningTable.headerPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleaningRows.map((row, i) => (
                      <tr key={i}>
                        <td>{t(`pricing.cleaningTable.${row.type}`)}</td>
                        <td>
                          {row.onRequest ? t('pricing.cleaningTable.onRequest') : (
                            <>
                              €{row.price}
                              <span className="price-vat-note">€{toInclVat(row.price)} {t('pricing.inclVat')}</span>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Aanvullende diensten */}
            <section className="product-section">
              <h2>{t('pricing.cleaningTable.additionalTitle')}</h2>
              <div className="cleaning-pricing-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('pricing.cleaningTable.headerService')}</th>
                      <th>{t('pricing.cleaningTable.headerPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalServiceRows.map((row, i) => (
                      <tr key={i}>
                        <td>{t(`pricing.cleaningTable.services.${row.key}`)}</td>
                        <td>
                          {row.priceKey ? t(`pricing.cleaningTable.${row.priceKey}`) : (
                            <>
                              {row.price}
                              <span className="price-vat-note">€{toInclVat(row.price)} {t('pricing.inclVat')}</span>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Opmerkingen */}
            <div className="pricing-notes">
              <h3>{t('pricing.cleaningTable.notesTitle')}</h3>
              <ul>
                {t('pricing.cleaningTable.notes', { returnObjects: true }).map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'automation' && (
          <div className="pricing-tab-panel automation-theme" key="automation-panel">
            <section className="product-section automation-features-section">
              <h2 className="pricing-hero-title">{t('pricing.automationTitle')}</h2>
              <p className="pricing-subtitle">{t('pricing.automationIntro')}</p>
              <p className="section-description">{t('pricing.automationDescription')}</p>
              <div className="automation-features-row">
                {t('pricing.automationFeatures', { returnObjects: true }).map((feature, i) => (
                  <div className="automation-feature-block" key={i}>
                    <div className="automation-feature-icon">{AUTOMATION_FEATURE_ICONS[i]}</div>
                    <h3 className="automation-feature-title"><span className="automation-feature-title-wrap">{feature.title}{feature.badge && <span className="automation-feature-badge">{feature.badge}</span>}</span></h3>
                    <p className="automation-feature-text">{feature.text}</p>
                  </div>
                ))}
              </div>
              <div className="tier-price-box">
                <strong>{t('pricing.automationPriceLabel')}</strong>
                <span>{t('pricing.automationPriceHint')}</span>
              </div>
              <div className="setup-option-box">
                <h3>{t('pricing.setupOptionTitle')}</h3>
                <p>{t('pricing.setupOptionIntro')}</p>
                <div className="setup-steps-row">
                  {setupSteps.slice(0, 2).map((step, i) => (
                    <div className="setup-step-block" key={i}>
                      <div className="setup-step-icon">{SETUP_STEP_ICONS[i]}</div>
                      <h3 className="setup-step-title">{step.title}</h3>
                      <p className="setup-step-text">{step.text}</p>
                    </div>
                  ))}
                </div>
                <div className="tier-price-box">
                  <strong>{t('pricing.setupOptionPriceLabel')}</strong>
                  <span>{t('pricing.setupOptionPriceHint')}</span>
                </div>
              </div>
            </section>

            {/* Wat je te zien krijgt in de app: featurelijst + een
                dashboard-mockup (nagebouwd in CSS, geen echte screenshot)
                die dezelfde kaarten toont als het echte Automation-overzicht
                in de app (bezettingsgraad, kanaalmix, aankomende in-/
                uitchecks). */}
            <section className="product-section">
              <h2>{t('pricing.automationAppTitle')}</h2>
              <p className="section-description">{t('pricing.automationAppIntro')}</p>
              <div className="automation-app-showcase">
                <ul className="tier-feature-list app-feature-list">
                  {t('pricing.automationAppFeatures', { returnObjects: true }).map((feature, i) => (
                    <li key={i}><span className="tier-check">✓</span> {feature}</li>
                  ))}
                </ul>

                <div className="app-mockup">
                  <div className="app-mockup-topbar">
                    <span className="app-mockup-dot app-mockup-dot-red"></span>
                    <span className="app-mockup-dot app-mockup-dot-yellow"></span>
                    <span className="app-mockup-dot app-mockup-dot-green"></span>
                    <span className="app-mockup-title">{t('pricing.automationMockup.windowTitle')}</span>
                  </div>
                  <div className="app-mockup-body">
                    <div className="app-mockup-cards">
                      <div className="app-mockup-card">
                        <div className="app-mockup-card-label">{t('pricing.automationMockup.occupancyLabel')}</div>
                        <div className="app-mockup-card-value">68%</div>
                      </div>
                      <div className="app-mockup-card">
                        <div className="app-mockup-card-label">{t('pricing.automationMockup.revenueLabel')}</div>
                        <div className="app-mockup-card-value">€6.240</div>
                      </div>
                      <div className="app-mockup-card">
                        <div className="app-mockup-card-label">{t('pricing.automationMockup.reviewsLabel')}</div>
                        <div className="app-mockup-card-value">4.8 / 5</div>
                      </div>
                    </div>
                    <div className="app-mockup-card app-mockup-card-full">
                      <div className="app-mockup-card-label">{t('pricing.automationMockup.channelMixLabel')}</div>
                      <div className="app-mockup-channel-row">
                        <span className="app-mockup-channel-name">{t('pricing.automationMockup.channelAirbnb')}</span>
                        <div className="app-mockup-bar-track"><div className="app-mockup-bar-fill" style={{ width: '54%' }}></div></div>
                        <span className="app-mockup-channel-count">54%</span>
                      </div>
                      <div className="app-mockup-channel-row">
                        <span className="app-mockup-channel-name">{t('pricing.automationMockup.channelBooking')}</span>
                        <div className="app-mockup-bar-track"><div className="app-mockup-bar-fill" style={{ width: '31%' }}></div></div>
                        <span className="app-mockup-channel-count">31%</span>
                      </div>
                      <div className="app-mockup-channel-row">
                        <span className="app-mockup-channel-name">{t('pricing.automationMockup.channelVrbo')}</span>
                        <div className="app-mockup-bar-track"><div className="app-mockup-bar-fill" style={{ width: '15%' }}></div></div>
                        <span className="app-mockup-channel-count">15%</span>
                      </div>
                    </div>
                    <div className="app-mockup-list">
                      <div className="app-mockup-list-title">{t('pricing.automationMockup.upcomingLabel')}</div>
                      {[
                        { property: t('pricing.automationMockup.property1'), guest: t('pricing.automationMockup.guest1'), dates: '12 aug → 15 aug' },
                        { property: t('pricing.automationMockup.property2'), guest: t('pricing.automationMockup.guest2'), dates: '13 aug → 16 aug' },
                      ].map((row) => (
                        <div className="app-mockup-list-row" key={row.property}>
                          <span className="app-mockup-list-property">{row.property}</span>
                          <span className="app-mockup-list-guest">{row.guest}</span>
                          <span className="app-mockup-list-dates">{row.dates}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Adviesgesprek */}
        <div className={`offer-section ${activeTab === 'toegangsbeheer' ? 'access-theme' : activeTab === 'setup' ? 'setup-theme' : activeTab === 'automation' ? 'automation-theme' : ''}`}>
          <h2>{t('pricing.requestAdvice')}</h2>
          <p>{t('pricing.offerText')}</p>
          <div className="offer-cta-row">
            <Link to="/contact" className="offer-btn">
              {t('pricing.advice')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Pricing;
